import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    OnDestroy,
} from '@angular/core';
import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
    FormGroup,
    FormControl,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import { DateFormatLocalizationService } from 'src/app/core/services/date-format-localization.service';
import { DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS, MatDateFormats, NativeDateAdapter } from '@angular/material/core';

export interface DateRange {
    from: Date | null;
    to: Date | null;
}

class DynamicDateAdapter extends NativeDateAdapter {
    constructor(private dateFormatService: DateFormatLocalizationService, locale: string) {
        super(locale);
    }

    override format(date: Date, displayFormat: object): string {
        const format = displayFormat as string | object;
        if (format === 'input' || format === 'dateA11yLabel') {
            return this.dateFormatService.formatDate(date);
        }
        return super.format(date, displayFormat);
    }

    override parse(value: string | number | Date | null | undefined): Date | null {
        if (typeof value === 'string' && value.trim()) {
            return this.dateFormatService.parseDate(value);
        }
        return super.parse(value);
    }
}

const DYNAMIC_DATE_FORMATS: MatDateFormats = {
    parse: {
        dateInput: 'input',
    },
    display: {
        dateInput: 'input',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'input',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

@Component({
    selector: 'app-date-range-input',
    templateUrl: './date-range-input.component.html',
    styleUrls: ['./date-range-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: DateRangeInputComponent,
            multi: true,
        },
        {
            provide: MAT_DATE_LOCALE,
            useFactory: (dateFormatService: DateFormatLocalizationService) =>
                dateFormatService.getMatDateLocale(),
            deps: [DateFormatLocalizationService],
        },
        {
            provide: DateAdapter,
            useClass: DynamicDateAdapter,
            deps: [DateFormatLocalizationService, MAT_DATE_LOCALE],
        },
        { provide: MAT_DATE_FORMATS, useValue: DYNAMIC_DATE_FORMATS },
    ],
})
export class DateRangeInputComponent implements ControlValueAccessor, OnInit, OnDestroy {
    @Input() label = 'common.date_range';
    @Input() required = false;
    @Input() disabled = false;
    @Input() minDate?: Date;
    @Input() maxDate?: Date;
    @Input() height = '41px';
    @Input() clearable = false;

    @Output() rangeChange = new EventEmitter<DateRange>();

    rangeGroup!: FormGroup<{
        start: FormControl<Date | null>;
        end: FormControl<Date | null>;
    }>;

    formatPlaceholder$ = this.dateFormatService.dateFormatConfig$.pipe(
        map(config => config.displayFormat.toUpperCase())
    );

    private readonly destroy$ = new Subject<void>();
    private onChange: (value: DateRange) => void = () => { };
    private onTouched: () => void = () => { };

    constructor(
        private dateFormatService: DateFormatLocalizationService,
        private readonly dateAdapter: DateAdapter<Date>
    ) { }

    ngOnInit(): void {
        this.dateFormatService.dateFormatConfig$
            .pipe(takeUntil(this.destroy$))
            .subscribe(config => {
                this.dateAdapter.setLocale(config.matDateLocale);
            });

        this.rangeGroup = new FormGroup({
            start: new FormControl<Date | null>(null),
            end: new FormControl<Date | null>(null),
        });

        this.rangeGroup.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((value) => {
                const range: DateRange = {
                    from: value.start || null,
                    to: value.end || null,
                };

                if (range.from && range.to) {
                    this.onChange(range);
                    this.rangeChange.emit(range);
                }
            });
    }

    writeValue(value: DateRange | null): void {
        if (this.rangeGroup) {
            if (value) {
                this.rangeGroup.patchValue(
                    {
                        start: value.from,
                        end: value.to,
                    },
                    { emitEvent: false }
                );
            } else {
                this.rangeGroup.reset();
            }
        }
    }

    registerOnChange(fn: (value: DateRange) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        if (this.rangeGroup) {
            if (isDisabled) {
                this.rangeGroup.disable({ emitEvent: false });
            } else {
                this.rangeGroup.enable({ emitEvent: false });
            }
        }
    }

    onDateRangeInput(): void {
        this.onTouched();
    }

    getFormattedDate(date: Date | null | undefined): string {
        if (!date) return '';
        return this.dateFormatService.formatDate(date);
    }

    clearDate(event: Event): void {
        event.stopPropagation();

        this.rangeGroup.reset(
            {
                start: null,
                end: null,
            },
            { emitEvent: false }
        );

        const clearedRange: DateRange = { from: null, to: null };
        this.onChange(clearedRange);
        this.rangeChange.emit(clearedRange);
        this.onTouched();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
